import { Alert, Box, Button, Chip, Stack, Typography } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import OrderOverview from '../../components/customer/orders/order-overview.component';
import OrderItems from '../../components/customer/orders/order-items.component';
import DeliveryNotes from '../../components/customer/orders/delivery-notes.component';
import Invoices from '../../components/customer/orders/invoices.component';
import { useMemo } from 'react';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { InfoContainer } from '@/components/base/info-container.component';
import {
  useGetOrder,
  useGetOrderRelated,
} from '@/api/documents/documents.hooks.ts';
import { resolveOrderStatus } from './order-status.util';

export default function ExternalOrderDetailsPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSuccessRedirect = useMemo(() => {
    return searchParams.has('success');
  }, [searchParams]);
  const {
    data: orderResponseData,
    isPending: isOrderResponseDataPending,
    isError: isOrderResponseDataError,
  } = useGetOrder(Number(orderId));

  // Live SAP enrichment (delivery notes, invoices, tracking, addresses) loads
  // separately so the order header + items render immediately from the DB.
  const { data: relatedData, isPending: isRelatedPending } = useGetOrderRelated(
    Number(orderId),
    undefined,
    { enabled: !isOrderResponseDataError },
  );

  if (isOrderResponseDataError) {
    return (
      <InfoContainer
        title="Ein Fehler ist aufgetreten"
        description="Bitte versuchen Sie es später erneut oder wenden Sie sich an Ihren Ansprechpartner."
        button={{
          text: 'Zurück zur Startseite',
          href: '/bp/c',
        }}
      />
    );
  }

  const order = orderResponseData?.order ?? null;
  const status = order ? resolveOrderStatus(order.documentStatus) : null;

  // Merge the fast local header with the live enrichment as it arrives.
  const enrichedOrder = order
    ? { ...order, ...(relatedData?.enrichment ?? {}) }
    : null;

  return (
    <Stack
      spacing={2}
      sx={{
        maxWidth: 'md',
        width: '100%',
        mx: 'auto',
      }}
    >
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: 'center', justifyContent: 'space-between' }}
      >
        <Button
          size="small"
          color="inherit"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/bp/c/orders')}
          sx={{ color: 'text.secondary' }}
        >
          Bestellungen
        </Button>
        {order ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              Auftrag #{order.docNum}
            </Typography>
            {status ? (
              <Chip
                size="small"
                label={status.label}
                color={status.color}
                variant="outlined"
                sx={{ fontWeight: 500 }}
              />
            ) : null}
          </Box>
        ) : null}
      </Stack>

      {isSuccessRedirect && !isOrderResponseDataPending ? (
        <Alert
          elevation={1}
          icon={<CheckIcon fontSize="inherit" />}
          severity="success"
        >
          Ihre Bestellung wurde erfolgreich entgegengenommen. Sie erhalten eine
          Bestätigung per E-Mail.
        </Alert>
      ) : null}
      <OrderOverview order={enrichedOrder} isPending={isOrderResponseDataPending} />
      {isOrderResponseDataPending ? null : (
        <>
          <OrderItems
            items={orderResponseData?.order.documentLines ?? []}
            isPending={isOrderResponseDataPending}
          />
          <DeliveryNotes
            deliveryNotes={relatedData?.deliveryNotes ?? []}
            isPending={isRelatedPending}
          />
          <Invoices
            invoices={relatedData?.invoices ?? []}
            isPending={isRelatedPending}
          />
        </>
      )}
    </Stack>
  );
}
